import { BaseSearchRequest } from '../../../models/overmind/search-request';
import { ReducerAction } from '../../../models/reducer-state';
import { EmployeeSearchResponse, PersonListItem } from '../../../models/view/employee-search-response';
import { PeopleCMSLayoutData, PeopleHeaderContentCMSLayoutData } from './people.cms.models';
import { ColumnConfiguration } from '../../../models/column-configuration';
import { PagePath } from '../../../navigation/navigation.enums';
import { Entity } from '../../../models';
import { SortField } from '../../../enums';
import { FocusPanelViewData } from '../../../models/focus-panel.models';
import { Breadcrumb } from '@keplerco/core';
import { Country } from '../../onboarding/classic/classic.onboarding.layout.models';

interface PeopleCMSState {
  pageTitle: string;
  crumbs: Breadcrumb[];
  request: BaseSearchRequest;
  data: EmployeeSearchResponse | undefined;

  // dialogs
  pendingInvites?: PersonListItem[];
  personToArchive?: PersonListItem;
  personToManage?: FocusPanelViewData<string | undefined>;
  personToDelete?: PersonListItem;

  // columns
  managingColumns?: boolean;
  columnConfiguration: ColumnConfiguration[];

  // filters
  departments: Entity[] | undefined;
  teams: Entity[] | undefined;
  countries: Country[] | undefined;
}

export const initialState: PeopleCMSState = {
  pageTitle: 'People',
  crumbs: [],
  request: { sortAscending: true, sortField: SortField.Name, pageSize: 15 },
  data: undefined,

  // columns
  columnConfiguration: [
    { label: 'Name', key: 'Name', required: true },
    { label: 'Role', key: 'Role', required: false, selected: true },
    { label: 'Department', key: 'Department', required: false, selected: true },
    { label: 'Team', key: 'Team', required: false, selected: true },
    { label: 'Line manager', key: 'Manager', required: false, selected: true },
    { label: 'Email', key: 'Email', required: false, selected: true },
    { label: 'Country', key: 'Country', required: false, selected: true },
    { label: 'Status', key: 'Status', required: false, selected: true },
    { label: 'Date Created', key: 'DateCreated', required: false, selected: true },
  ],

  // filters
  departments: undefined,
  teams: undefined,
  countries: undefined,
};

export enum PeopleCMSActionTypes {
  SetHeader = 0,
  SetRequest = 1,
  SetData = 2,

  // dialogs
  SetBulkUpload = 3,
  SetPendingInvites = 4,
  SetPersonToArchive = 5,
  SetPersonToManage = 6,

  // columns
  ManageColumns = 7,
  EditColumnConfiguration = 8,

  // filters
  SetDepartments = 9,
  SetTeams = 10,
  SetCountries = 11,
  SetRoles = 12,
  SetPersonToDelete = 13,
}

type PeopleCMSActions =
  | ReducerAction<PeopleCMSActionTypes.SetHeader, { payload: PeopleHeaderContentCMSLayoutData }>
  | ReducerAction<PeopleCMSActionTypes.SetRequest, { payload: BaseSearchRequest }>
  | ReducerAction<PeopleCMSActionTypes.SetData, { payload: PeopleCMSLayoutData }>

  // dialogs
  | ReducerAction<PeopleCMSActionTypes.SetPendingInvites, { payload: PersonListItem[] | undefined }>
  | ReducerAction<PeopleCMSActionTypes.SetPersonToArchive, { payload: PersonListItem | undefined }>
  | ReducerAction<PeopleCMSActionTypes.SetPersonToManage, { payload: FocusPanelViewData<string | undefined> }>
  | ReducerAction<PeopleCMSActionTypes.SetPersonToDelete, { payload: PersonListItem | undefined }>

  // columns
  | ReducerAction<PeopleCMSActionTypes.EditColumnConfiguration, { payload: ColumnConfiguration[] }>
  | ReducerAction<PeopleCMSActionTypes.ManageColumns, { payload: { open: boolean; reset?: boolean } }>

  // filters
  | ReducerAction<PeopleCMSActionTypes.SetDepartments, { payload: Entity[] | undefined }>
  | ReducerAction<PeopleCMSActionTypes.SetTeams, { payload: Entity[] | undefined }>
  | ReducerAction<PeopleCMSActionTypes.SetCountries, { payload: Country[] | undefined }>;

export function reducer(state: PeopleCMSState, action: PeopleCMSActions): PeopleCMSState {
  if (action.type === PeopleCMSActionTypes.SetHeader) {
    let crumbs: Breadcrumb[] = [
      { name: 'User Management', url: PagePath.userManagementBase },
      { name: 'People', url: window.location.pathname },
    ];

    if (!!action.payload.teamSlug) {
      crumbs = [
        { name: 'User Management', url: PagePath.userManagementBase },
        { name: 'Teams', url: `${PagePath.userManagementBase}${PagePath.userManagementTeams.replace(':companySlug', action.payload.companySlug)}` },
        { name: 'People', url: window.location.pathname },
      ];
    } else if (!!action.payload.departmentSlug) {
      crumbs = [
        { name: 'User Management', url: PagePath.userManagementBase },
        { name: 'Departments', url: `${PagePath.userManagementBase}${PagePath.userManagementDepartments.replace(':companySlug', action.payload.companySlug)}` },
        { name: 'People', url: window.location.pathname },
      ];
    }

    return {
      ...state,
      pageTitle: 'People',
      crumbs: crumbs,
    };
  }

  if (action.type === PeopleCMSActionTypes.SetRequest) {
    return { ...state, request: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetData) {
    return {
      ...state,
      data: action.payload.data,
    };
  }

  // dialogs

  if (action.type === PeopleCMSActionTypes.SetPendingInvites) {
    return { ...state, pendingInvites: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetPersonToArchive) {
    return { ...state, personToArchive: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetPersonToManage) {
    return { ...state, personToManage: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetPersonToDelete) {
    return { ...state, personToDelete: action.payload };
  }

  // columns
  if (action.type === PeopleCMSActionTypes.ManageColumns) {
    if (!action.payload.reset) return { ...state, managingColumns: action.payload.open };
    else if (action.payload.reset) return { ...state, managingColumns: action.payload.open, columnConfiguration: [...initialState.columnConfiguration] };
  }

  if (action.type === PeopleCMSActionTypes.EditColumnConfiguration) {
    return { ...state, columnConfiguration: action.payload };
  }

  // filters
  if (action.type === PeopleCMSActionTypes.SetDepartments) {
    return { ...state, departments: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetTeams) {
    return { ...state, teams: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetCountries) {
    return { ...state, countries: action.payload };
  }

  return state;
}

